/**
 * Shopify Refresh Theme - Product Minimum Quantity Enforcer
 * Handles product pages, quick add modals, and cart drawer
 * Minimum is set at product level and applies to all variants
 */

class ProductMinimumQuantity {
  constructor() {
    this._pendingModalProductId = null;
    this._minimumCache = {}; // Cache minimums we find
    this._itemViolations = []; // Pre-computed list of cart items below their minimum
    this._cartFetchInFlight = null;
    this.init();
  }

  // Resolves the /cart/add form for a given quantity input, handling both
  // descendant inputs and Dawn's `form="..."` attribute pattern where the
  // input lives outside its form element.
  findCartAddForm(input) {
    if (!input) return null;
    let form = input.closest('form[action*="/cart/add"]');
    if (form) return form;
    // Native: input.form resolves the form attribute by ID.
    if (input.form && input.form.action && input.form.action.indexOf('/cart/add') !== -1) {
      return input.form;
    }
    return null;
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    console.log('ProductMinimumQuantity initialized');

    // Cache all product minimums from the page
    this.cacheProductMinimums();

    // Handle product pages and quick add modals
    this.enforceMinimumOnQuantityInputs();
    this.interceptQuantityInputs();
    this.validateAddToCart();
    this.validateCartDrawer();
    this.subscribeToCartUpdates();
    this.refreshItemViolations();
    this.validateCheckout();
    this.setupQuickAddModalListener();
    this.observeVariantChanges();
    
    // Listen for dynamic content (modals, etc.)
    this.observeDynamicContent();
    
    // Set initial minimums after a short delay to ensure everything is loaded
    setTimeout(() => this.setInitialMinimums(), 500);
    
    // Also run immediately for cart page
    if (window.location.pathname.includes('/cart')) {
      console.log('Cart page detected, initializing immediately');
      this.setInitialMinimums();
      this.updateCartButtonStates();
    }

    // Re-initialize on back/forward cache restore (e.g. returning from checkout)
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        console.log('Page restored from bfcache, re-initializing minimums');
        this.cacheProductMinimums();
        setTimeout(() => {
          this.setInitialMinimums();
          this.updateCartButtonStates();
        }, 200);
      }
    });
  }

  // Cache all product minimums from product cards on page load
  cacheProductMinimums() {
    document.querySelectorAll('[data-product-id][data-min-quantity]').forEach(el => {
      const productId = parseInt(el.dataset.productId);
      const minQty = parseInt(el.dataset.minQuantity);
      if (productId && minQty) {
        this._minimumCache[productId] = minQty;
        console.log(`Cached minimum ${minQty} for product ${productId}`);
      }
    });
    
    // ALSO cache from variant metafields if present in JSON
    document.querySelectorAll('script[type="application/json"][data-product-id]').forEach(script => {
      try {
        const productId = parseInt(script.dataset.productId);
        const product = JSON.parse(script.textContent);
        
        // Check if product has min_quantity metafield
        if (product.min_quantity) {
          this._minimumCache[productId] = parseInt(product.min_quantity);
          console.log(`Cached minimum ${product.min_quantity} for product ${productId} from JSON`);
        }
        
        // Also check variants for their own minimums
        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach(variant => {
            if (variant.min_quantity) {
              // Use variant ID as key for variant-specific minimums
              this._minimumCache[`variant_${variant.id}`] = parseInt(variant.min_quantity);
              console.log(`Cached minimum ${variant.min_quantity} for variant ${variant.id}`);
            }
          });
        }
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    });
  }

  // Get minimum quantity for a product
  getMinimumForProduct(productId, variantId = null) {
    // First check if we have a variant-specific minimum
    if (variantId && this._minimumCache[`variant_${variantId}`]) {
      const min = this._minimumCache[`variant_${variantId}`];
      console.log(`Found variant minimum for variant ${variantId}: ${min}`);
      return min;
    }
    
    // Check cache first (fastest)
    if (this._minimumCache[productId]) {
      console.log(`Found minimum for product ${productId}: ${this._minimumCache[productId]} (from cache)`);
      return this._minimumCache[productId];
    }

    // Try to find it in product data attribute (from product cards)
    const productElement = document.querySelector(`[data-product-id="${productId}"]`);
    if (productElement && productElement.dataset.minQuantity) {
      const min = parseInt(productElement.dataset.minQuantity) || 1;
      console.log(`Found minimum for product ${productId}: ${min} (from data attribute)`);
      // Cache it for next time
      this._minimumCache[productId] = min;
      return min;
    }

    // Try to find it in product JSON (from product page)
    const productJson = document.querySelector(`script[type="application/json"][data-product-id="${productId}"]`);
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        const min = product.min_quantity || 1;
        console.log(`Found minimum from JSON for product ${productId}: ${min}`);
        // Cache it
        this._minimumCache[productId] = min;
        return min;
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }

    // Try to fetch from the product card wrapper (collection pages)
    const cardWrapper = document.querySelector(`.card-wrapper[data-product-id="${productId}"]`);
    if (cardWrapper && cardWrapper.dataset.minQuantity) {
      const min = parseInt(cardWrapper.dataset.minQuantity) || 1;
      console.log(`Found minimum for product ${productId}: ${min} (from card wrapper)`);
      // Cache it
      this._minimumCache[productId] = min;
      return min;
    }

    console.log(`No minimum found for product ${productId}, defaulting to 1`);
    return 1;
  }

  // Intercept all quantity input interactions
  interceptQuantityInputs() {
    // Use capture phase to catch events before other handlers
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button.quantity__button');
      if (!button) return;

      const quantityInput = button.closest('quantity-input, .quantity')?.querySelector('input[name="quantity"], input.quantity__input');
      if (!quantityInput) return;

      // Get the form to determine product ID and minimum
      const form = this.findCartAddForm(quantityInput);
      if (!form) return;

      const productId = this.getProductIdFromForm(form);
      if (!productId) return;

      const minimum = this.getMinimumForProduct(productId);
      const currentQty = parseInt(quantityInput.value) || 0;
      const isMinus = button.getAttribute('name') === 'minus';

      // Only prevent if clicking minus would take us below minimum
      if (isMinus && currentQty <= minimum) {
        console.log(`Preventing minus click - would go below minimum (current: ${currentQty}, min: ${minimum})`);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.showMessage(quantityInput, `Minimum quantity is ${minimum}`, 'warning');
        return false;
      }

      // Wait for the default handler to run, then enforce minimum
      setTimeout(() => {
        this.enforceMinimumOnInput(quantityInput);
      }, 50);
    }, true);

    // Also intercept the QuantityInput custom element if it exists
    this.interceptQuantityInputElement();
  }

  // Intercept the custom QuantityInput element
  interceptQuantityInputElement() {
    // Wait for custom elements to be defined
    if (customElements.get('quantity-input')) {
      const QuantityInputProto = customElements.get('quantity-input').prototype;
      
      // Wrap the onChange method if it exists
      if (QuantityInputProto.onChange) {
        const originalOnChange = QuantityInputProto.onChange;
        QuantityInputProto.onChange = function() {
          originalOnChange.call(this);
          
          // Apply minimum after the original change
          const input = this.querySelector('input[name="quantity"]');
          if (input) {
            const instance = window.productMinimumQuantity;
            if (instance) {
              setTimeout(() => instance.enforceMinimumOnInput(input), 10);
            }
          }
        };
      }

      // Wrap the onButtonClick method if it exists
      if (QuantityInputProto.onButtonClick) {
        const originalOnButtonClick = QuantityInputProto.onButtonClick;
        QuantityInputProto.onButtonClick = function(event) {
          const result = originalOnButtonClick.call(this, event);
          
          const input = this.querySelector('input[name="quantity"]');
          if (input) {
            const instance = window.productMinimumQuantity;
            if (instance) {
              setTimeout(() => instance.enforceMinimumOnInput(input), 10);
            }
          }
          
          return result;
        };
      }
    } else {
      // Try again after custom elements are loaded
      setTimeout(() => this.interceptQuantityInputElement(), 1000);
    }
  }

  // Enforce minimum on a specific input
  enforceMinimumOnInput(input) {
    if (!input) return;

    const form = this.findCartAddForm(input);
    if (form) {
      // Product page form
      const productId = this.getProductIdFromForm(form);
      if (productId) {
        const minimum = this.getMinimumForProduct(productId);
        const currentQty = parseInt(input.value) || 0;
        
        console.log(`Product form - Current: ${currentQty}, Minimum: ${minimum}`);
        
        if (currentQty < minimum) {
          input.value = minimum;
          
          // Dispatch events to notify other components
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          
          this.showMessage(input, `Minimum quantity is ${minimum}`, 'warning');
        }
        
        // Disable minus button if at minimum
        const quantityWrapper = input.closest('quantity-input');
        if (quantityWrapper) {
          const minusBtn = quantityWrapper.querySelector('button[name="minus"]');
          if (minusBtn) {
            if (parseInt(input.value) <= minimum) {
              minusBtn.disabled = true;
              minusBtn.setAttribute('disabled', 'disabled');
            } else {
              minusBtn.disabled = false;
              minusBtn.removeAttribute('disabled');
            }
          }
        }
      }
    } else {
      // Might be cart drawer
      const cartItem = input.closest('[data-cart-item]');
      if (cartItem) {
        const productId = parseInt(cartItem.dataset.productId);
        if (productId) {
          const minimum = this.getMinimumForProduct(productId);
          const currentQty = parseInt(input.value) || 0;
          
          console.log(`Cart item - Current: ${currentQty}, Minimum: ${minimum}`);
          
          if (currentQty > 0 && currentQty < minimum) {
            input.value = minimum;
            this.showMessage(input, `Minimum quantity is ${minimum}`, 'warning');
            
            // Trigger cart update
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          
          // Update button states
          this.updateCartButtonStates();
        }
      }
    }
  }

  // Enforce minimum on quantity inputs
  enforceMinimumOnQuantityInputs() {
    // Handle quantity input changes
    document.addEventListener('change', (e) => {
      const input = e.target;
      if (!input.matches('input[name="quantity"]')) return;

      this.enforceMinimumOnInput(input);
    });

    // Handle input events (manual typing)
    document.addEventListener('input', (e) => {
      const input = e.target;
      if (!input.matches('input[name="quantity"]')) return;

      const form = this.findCartAddForm(input);
      if (!form) return;

      const productId = this.getProductIdFromForm(form);
      if (!productId) return;

      const minimum = this.getMinimumForProduct(productId);
      const currentQty = parseInt(input.value) || 0;

      // As user types, if they go below minimum, auto-correct
      if (currentQty > 0 && currentQty < minimum) {
        input.value = minimum;
      }
      
      // Always update button states after any input
      this.updateProductFormButtonStates(input);
    });
  }
  
  // Update button states for a product form input
  updateProductFormButtonStates(input) {
    const form = this.findCartAddForm(input);
    if (!form) return;
    
    const productId = this.getProductIdFromForm(form);
    if (!productId) return;
    
    const minimum = this.getMinimumForProduct(productId);
    const currentQty = parseInt(input.value) || 0;
    
    const quantityWrapper = input.closest('quantity-input');
    if (quantityWrapper) {
      const minusBtn = quantityWrapper.querySelector('button[name="minus"]');
      if (minusBtn) {
        // Disable only if at or below minimum AND minimum > 1
        if (currentQty <= minimum && minimum > 1) {
          minusBtn.disabled = true;
          minusBtn.setAttribute('disabled', 'disabled');
        } else {
          minusBtn.disabled = false;
          minusBtn.removeAttribute('disabled');
        }
      }
    }
  }

  observeVariantChanges() {
    // Listen for variant change events (Shopify typically uses this)
    document.addEventListener('variant:change', (e) => {
      console.log('Variant changed, re-applying minimums');
      setTimeout(() => this.setInitialMinimums(), 100);
    });
    
    // Also listen for changes to variant selectors
    document.addEventListener('change', (e) => {
      const select = e.target;
      
      // Check if it's a variant selector
      if (select.matches('[name="id"], select[id*="variant"], .product-form__input input[type="radio"]')) {
        console.log('Variant selector changed, re-applying minimums');
        setTimeout(() => this.setInitialMinimums(), 150);
      }
    });
    
    // Watch for quantity input value changes from external sources (like variant switching)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const input = mutation.target;
          if (input.matches('input[name="quantity"]')) {
            console.log('Quantity input value attribute changed externally');
            setTimeout(() => this.enforceMinimumOnInput(input), 50);
          }
        }
      });
    });
    
    // Observe all quantity inputs
    document.querySelectorAll('input[name="quantity"]').forEach(input => {
      observer.observe(input, { 
        attributes: true,
        attributeFilter: ['value']
      });
    });
    
    // Re-observe when new inputs are added (for modals)
    const contentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const newInputs = node.querySelectorAll ? node.querySelectorAll('input[name="quantity"]') : [];
            newInputs.forEach(input => {
              observer.observe(input, { 
                attributes: true,
                attributeFilter: ['value']
              });
            });
          }
        });
      });
    });
    
    contentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Set initial minimums on forms
  setInitialMinimums() {
    console.log('Setting initial minimums...');
    
    // Product forms (including modals)
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    console.log(`Found ${forms.length} product forms`);
    
    forms.forEach(form => {
      const formId = form.getAttribute('id');
      const productId = this.getProductIdFromForm(form);
      
      // Try to find quantity input - it might be inside OR outside the form (linked via form attribute)
      let qtyInput = form.querySelector('input[name="quantity"]');
      if (!qtyInput && formId) {
        // Look for input with matching form attribute
        qtyInput = document.querySelector(`input[name="quantity"][form="${formId}"]`);
      }
      
      console.log(`Form: ${formId || 'no-id'}, Product ID: ${productId}, Has qty input: ${!!qtyInput}`);
      
      if (productId && qtyInput) {
        const minimum = this.getMinimumForProduct(productId);
        const currentValue = parseInt(qtyInput.value) || 1;
        
        // Check if this is a modal form
        const isModal = form.closest('.quick-add-modal, quick-add-modal, [role="dialog"]');
        if (isModal) {
          console.log(`MODAL FORM - Setting minimum ${minimum} for product ${productId} (current: ${currentValue})`);
        } else {
          console.log(`Setting minimum ${minimum} for product ${productId}`);
        }
        
        // Set the value if below minimum
        if (currentValue < minimum) {
          qtyInput.value = minimum;
          
          // Also trigger change event to update any listeners
          qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        qtyInput.min = minimum;
        qtyInput.setAttribute('data-min', minimum);
        qtyInput.setAttribute('min', minimum);
        
        // Also update the quantity-input wrapper if it exists
        const quantityInputWrapper = qtyInput.closest('quantity-input');
        if (quantityInputWrapper) {
          quantityInputWrapper.dataset.min = minimum;
          
          // Force update the minus button state
          const minusBtn = quantityInputWrapper.querySelector('button[name="minus"]');
          if (minusBtn) {
            const currentValue = parseInt(qtyInput.value);
            
            // Disable if at minimum
            if (currentValue <= minimum) {
              minusBtn.disabled = true;
              minusBtn.setAttribute('disabled', 'disabled');
              minusBtn.classList.add('disabled');
            } else {
              minusBtn.disabled = false;
              minusBtn.removeAttribute('disabled');
              minusBtn.classList.remove('disabled');
            }
          }
        }
      }
    });

    // Cart items (drawer and main page)
    document.querySelectorAll('[data-cart-item], .cart-item').forEach(cartItem => {
      const productId = parseInt(cartItem.dataset.productId);
      const qtyInput = cartItem.querySelector('input[name="updates[]"]');
      
      if (productId && qtyInput) {
        const minimum = this.getMinimumForProduct(productId);
        const currentQty = parseInt(qtyInput.value) || 0;
        
        console.log(`Setting minimum ${minimum} for cart product ${productId} (current: ${currentQty})`);
        
        qtyInput.setAttribute('data-min', minimum);
        qtyInput.setAttribute('min', minimum);
        qtyInput.min = minimum;
        
        // For cart, disable minus button if at minimum
        const minusBtn = cartItem.querySelector('button[name="minus"]');
        if (minusBtn && currentQty <= minimum) {
          minusBtn.disabled = true;
          minusBtn.setAttribute('disabled', 'disabled');
          minusBtn.classList.add('disabled');
          console.log(`Disabled minus button for product ${productId} - at minimum`);
        }
      }
    });
  }

  // Get product ID from form
  getProductIdFromForm(form) {
    // Check form data attribute
    if (form.dataset.productId) {
      return parseInt(form.dataset.productId);
    }

    // Check for product ID input
    const productInput = form.querySelector('[name="product-id"]');
    if (productInput) {
      return parseInt(productInput.value);
    }

    // Check parent product-info element (common in modals)
    const productInfo = form.closest('product-info');
    if (productInfo && productInfo.dataset.productId) {
      return parseInt(productInfo.dataset.productId);
    }

    // Check parent elements with data-product-id
    const productContainer = form.closest('[data-product-id]');
    if (productContainer) {
      return parseInt(productContainer.dataset.productId);
    }

    // Check if we're in a modal and have a pending product ID
    const modal = form.closest('.quick-add-modal, quick-add-modal, [role="dialog"]');
    if (modal) {
      // First try pending ID
      if (this._pendingModalProductId) {
        return parseInt(this._pendingModalProductId);
      }
      
      // Try to find product ID from the modal ID
      const modalElement = form.closest('quick-add-modal[id*="QuickAdd"]');
      if (modalElement) {
        const modalId = modalElement.id;
        const match = modalId?.match(/QuickAdd-(\d+)/);
        if (match) {
          return parseInt(match[1]);
        }
      }
    }

    return null;
  }

  // Validate add to cart submissions
  validateAddToCart() {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.matches('form[action*="/cart/add"]')) return;

      // Dawn renders the quantity input outside the form (linked via the input's
      // form="..." attribute), so form.querySelector() doesn't find it. Fall back
      // to a document-level lookup by the form attribute.
      let qtyInput = form.querySelector('input[name="quantity"]');
      if (!qtyInput && form.id) {
        qtyInput = document.querySelector(`input[name="quantity"][form="${form.id}"]`);
      }
      const productId = this.getProductIdFromForm(form);

      if (!qtyInput || !productId) return;

      const quantity = parseInt(qtyInput.value) || 0;
      const minimum = this.getMinimumForProduct(productId);

      console.log(`Validating form submission - Quantity: ${quantity}, Minimum: ${minimum}`);

      if (quantity < minimum) {
        e.preventDefault();
        e.stopPropagation();
        this.showMessage(
          form, 
          `Please add at least ${minimum} of this product`, 
          'error'
        );
        qtyInput.value = minimum;
        qtyInput.focus();
      }
    });
  }

  // Validate cart drawer quantities
  validateCartDrawer() {
    // MORE AGGRESSIVE: Intercept BEFORE Shopify's handler using capture phase with higher priority
    document.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('[data-cart-item] button[name="minus"], .cart-item button[name="minus"]');
      if (!minusBtn) return;
      
      const cartItem = minusBtn.closest('[data-cart-item], .cart-item');
      if (!cartItem) return;
      
      const productId = parseInt(cartItem.dataset.productId);
      if (!productId) return;
      
      const minimum = this.getMinimumForProduct(productId);
      const qtyInput = cartItem.querySelector('input[name="updates[]"]');
      const currentQty = parseInt(qtyInput?.value) || 0;
      
      console.log(`CART MINUS CLICKED - Product: ${productId}, Current: ${currentQty}, Minimum: ${minimum}`);
      
      // CRITICAL: Stop the event if at or below minimum
      if (currentQty <= minimum) {
        console.log('BLOCKING MINUS BUTTON - at minimum');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Make absolutely sure button is disabled
        minusBtn.disabled = true;
        minusBtn.setAttribute('disabled', 'disabled');
        minusBtn.classList.add('disabled');
        
        this.showMessage(qtyInput, `Minimum quantity is ${minimum}`, 'warning');
        return false;
      }
    }, true); // CAPTURE PHASE - runs before any other handlers
    
    // Watch for cart updates
    document.addEventListener('change', (e) => {
      const input = e.target;
      if (!input.matches('input[name="updates[]"]')) return;

      const cartItem = input.closest('[data-cart-item], .cart-item');
      if (!cartItem) return;

      const productId = parseInt(cartItem.dataset.productId);
      const quantity = parseInt(input.value) || 0;
      
      if (!productId) return;

      const minimum = this.getMinimumForProduct(productId);

      console.log(`Cart input changed - Product: ${productId}, Quantity: ${quantity}, Minimum: ${minimum}`);

      if (quantity > 0 && quantity < minimum) {
        // Prevent loop with flag
        if (!input.dataset.adjusting) {
          input.dataset.adjusting = 'true';
          input.value = minimum;
          this.showMessage(input, `Minimum quantity is ${minimum}`, 'warning');
          
          // Trigger change to update cart
          setTimeout(() => {
            input.dispatchEvent(new Event('change', { bubbles: true }));
            delete input.dataset.adjusting;
          }, 100);
        }
      }
      
      // Always update button states after change
      setTimeout(() => this.updateCartButtonStates(), 150);
    });
    
    // Continuously enforce button states
    setInterval(() => this.updateCartButtonStates(), 150);
  }
  
  // Update cart button states (both drawer and main cart page)
  updateCartButtonStates() {
    // Cart drawer items
    document.querySelectorAll('[data-cart-item]').forEach(cartItem => {
      const productId = parseInt(cartItem.dataset.productId);
      if (!productId) return;
      
      const minimum = this.getMinimumForProduct(productId);
      const qtyInput = cartItem.querySelector('input[name="updates[]"]');
      const minusBtn = cartItem.querySelector('button[name="minus"]');
      
      if (qtyInput && minusBtn) {
        const currentQty = parseInt(qtyInput.value) || 0;
        
        if (currentQty <= minimum) {
          minusBtn.disabled = true;
          minusBtn.setAttribute('disabled', 'disabled');
          minusBtn.classList.add('disabled');
          // Also add pointer-events none to be extra safe
          minusBtn.style.pointerEvents = 'none';
        } else {
          minusBtn.disabled = false;
          minusBtn.removeAttribute('disabled');
          minusBtn.classList.remove('disabled');
          minusBtn.style.pointerEvents = '';
        }
      }
    });
    
    // Main cart page items
    document.querySelectorAll('.cart-item').forEach(cartItem => {
      const productId = parseInt(cartItem.dataset.productId);
      if (!productId) return;
      
      const minimum = this.getMinimumForProduct(productId);
      const qtyInput = cartItem.querySelector('input[name="updates[]"]');
      const minusBtn = cartItem.querySelector('button[name="minus"]');
      
      if (qtyInput && minusBtn) {
        const currentQty = parseInt(qtyInput.value) || 0;
        
        if (currentQty <= minimum) {
          minusBtn.disabled = true;
          minusBtn.setAttribute('disabled', 'disabled');
          minusBtn.classList.add('disabled');
          minusBtn.style.pointerEvents = 'none';
        } else {
          minusBtn.disabled = false;
          minusBtn.removeAttribute('disabled');
          minusBtn.classList.remove('disabled');
          minusBtn.style.pointerEvents = '';
        }
      }
    });
  }

  // Keep an up-to-date list of cart items below their minimum so that the
  // checkout-time guard can decide synchronously inside the click/submit
  // handler. Doing the fetch inside the event handler (the prior approach)
  // was a no-op because preventDefault on a finished event has no effect.
  subscribeToCartUpdates() {
    const refresh = () => this.refreshItemViolations();
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try { subscribe(PUB_SUB_EVENTS.cartUpdate, refresh); } catch (e) {}
    }
    document.addEventListener('cart:updated', refresh);
    document.addEventListener('cart-drawer:open', refresh);
  }

  refreshItemViolations() {
    if (this._cartFetchInFlight) return this._cartFetchInFlight;
    this._cartFetchInFlight = fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const violations = [];
        (cart.items || []).forEach(item => {
          const minimum = this.getMinimumForProduct(item.product_id, item.variant_id);
          if (item.quantity < minimum) {
            violations.push({
              title: item.product_title,
              current: item.quantity,
              minimum
            });
          }
        });
        this._itemViolations = violations;
      })
      .catch(() => { /* leave previous state */ })
      .finally(() => { this._cartFetchInFlight = null; });
    return this._cartFetchInFlight;
  }

  // Block checkout if any cart item is below its product minimum.
  // Uses cached state populated by refreshItemViolations — the click handler
  // makes a synchronous decision so preventDefault actually takes effect.
  validateCheckout() {
    const block = (e) => {
      if (this._itemViolations.length === 0) return false;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.showCheckoutError(this._itemViolations);
      // Refresh in the background so the next attempt reflects any user fixes.
      this.refreshItemViolations();
      return true;
    };

    // Capture phase so we run before PickEasy's listeners on the form.
    document.addEventListener('click', (e) => {
      const checkoutBtn = e.target.closest('[name="checkout"], [href*="/checkout"], .checkout-button, #CartDrawer-Checkout');
      if (!checkoutBtn) return;
      block(e);
    }, true);

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form || (form.id !== 'CartDrawer-Form' && form.id !== 'cart')) return;
      block(e);
    }, true);
  }
  
  // Helper method to proceed to checkout
  proceedToCheckout(checkoutBtn) {
    console.log('Proceeding to checkout...', checkoutBtn);
    
    if (checkoutBtn.tagName === 'BUTTON') {
      // It's a button - try to submit the form
      const form = checkoutBtn.closest('form');
      if (form) {
        console.log('Submitting form');
        form.submit();
      } else {
        // No form, redirect to checkout
        console.log('No form found, redirecting to checkout');
        window.location.href = '/checkout';
      }
    } else if (checkoutBtn.tagName === 'A') {
      // It's a link - follow the href
      console.log('Following link to:', checkoutBtn.href);
      window.location.href = checkoutBtn.href;
    } else {
      // Unknown type, just go to checkout
      console.log('Unknown button type, redirecting to checkout');
      window.location.href = '/checkout';
    }
  }

  // Show checkout error with details
  showCheckoutError(violations) {
    let message = 'Cannot proceed to checkout. Minimum quantities not met:\n\n';
    violations.forEach(v => {
      const variantInfo = v.variant ? ` (${v.variant})` : '';
      message += `• ${v.title}${variantInfo}: ${v.current} in cart, minimum is ${v.minimum}\n`;
    });

    alert(message);
  }

  // Show message near element
  showMessage(element, text, type = 'info') {
    // Remove any existing custom messages first
    const existing = document.querySelector('.qty-minimum-message');
    if (existing) existing.remove();

    // Try to use the theme's built-in error container for cart items
    const cartItem = element?.closest('.cart-item, [data-cart-item]');
    if (cartItem) {
      const errorText = cartItem.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = text;
        const errorContainer = cartItem.querySelector('.cart-item__error');
        if (errorContainer) errorContainer.classList.remove('hidden');

        setTimeout(() => {
          errorText.textContent = '';
          if (errorContainer) errorContainer.classList.add('hidden');
        }, 4000);
        return;
      }
    }

    // Fallback for product page forms — append below the quantity-input wrapper
    const container = element?.closest('quantity-input')?.parentElement
      || element?.parentElement;

    const message = document.createElement('div');
    message.className = `qty-minimum-message qty-minimum-${type}`;
    message.textContent = text;
    message.style.cssText = `
      display: block;
      width: 100%;
      margin-top: 8px;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      box-sizing: border-box;
      clear: both;
      ${type === 'error' ? 'background: #fee; color: #c33; border: 1px solid #fcc;' : ''}
      ${type === 'warning' ? 'background: #fff4e5; color: #663c00; border: 1px solid #ffe0b2;' : ''}
      ${type === 'info' ? 'background: #e3f2fd; color: #01579b; border: 1px solid #bbdefb;' : ''}
    `;

    container?.appendChild(message);
    setTimeout(() => message.remove(), 4000);
  }

  // Setup quick-add modal listener
  setupQuickAddModalListener() {
    // Listen for modal open events
    document.addEventListener('click', (e) => {
      const modalOpener = e.target.closest('modal-opener');
      if (modalOpener) {
        // Get product ID from the modal opener or its parent
        const productId = modalOpener.dataset.productId || 
                         modalOpener.closest('[data-product-id]')?.dataset.productId;
        
        if (productId) {
          console.log(`Modal opening for product ${productId}`);
          // Store it temporarily so we can use it when modal loads
          this._pendingModalProductId = productId;
          
          // Wait for modal content to load
          setTimeout(() => {
            console.log('Setting minimums for modal');
            this.setInitialMinimums();
          }, 300);
          
          setTimeout(() => {
            this.setInitialMinimums();
          }, 600);
          
          // Clear after a longer timeout
          setTimeout(() => {
            this._pendingModalProductId = null;
          }, 2000);
        }
      }
    });
  }

  // Observe for dynamically loaded content (modals, etc.)
  observeDynamicContent() {
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      let cartDrawerUpdated = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Re-initialize for new modals/content
            if (node.matches('.quick-add-modal, quick-add-modal, [role="dialog"], form[action*="/cart/add"]') || 
                node.querySelector('.quick-add-modal, quick-add-modal, [role="dialog"], form[action*="/cart/add"]')) {
              shouldUpdate = true;
            }
            
            // Check for cart drawer updates
            if (node.matches('[data-cart-item], cart-drawer-items, .cart-drawer') ||
                node.querySelector('[data-cart-item], cart-drawer-items, .cart-drawer')) {
              cartDrawerUpdated = true;
            }
          }
        });
      });
      
      if (shouldUpdate) {
        setTimeout(() => {
          console.log('Content changed, updating minimums');
          this.setInitialMinimums();
        }, 100);
      }
      
      if (cartDrawerUpdated) {
        setTimeout(() => {
          console.log('Cart drawer updated, updating button states');
          this.updateCartButtonStates();
        }, 150);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize and make globally accessible
window.productMinimumQuantity = new ProductMinimumQuantity();
